/**
 * Storefront theme tokens — the single contract between the two apps.
 *
 * `storefront_sites.theme` is untyped jsonb; this module is its schema. The
 * Branding settings screen in `apps/web` writes it, the storefront reads it
 * and emits the tokens as CSS custom properties on the tenant's `<html>`,
 * the templates are authored in it, and the Puck canvas applies the same
 * variables so the editor shows the real palette rather than the dashboard's.
 *
 * There is deliberately no second copy of this vocabulary. A dashboard-side
 * fork of it used to exist and spoke a different dialect — numeric font
 * weights, a pixel `containerWidth`, `destructive` in place of `sale` — and
 * because this schema was strict, every theme that fork saved failed to
 * parse here and was silently replaced by `DEFAULT_THEME`. A merchant's
 * branding never reached their storefront. Hence the two rules below.
 *
 * RULE 1 — every leaf carries `.catch()`. One unknown token must never blank
 * a whole design; it falls back on its own and the rest of the theme
 * survives. The editor only submits values it has already normalised, so the
 * lenient parse is a repair path, not a validation hole.
 *
 * RULE 2 — `migrateTheme()` translates the retired dialect on read, so the
 * themes that fork already wrote recover their colours instead of resetting.
 *
 * Client-safe: schema + pure helpers only.
 */
import * as z from 'zod';

/* -------------------------------------------------------------------------
 * Colour helpers
 * ---------------------------------------------------------------------- */

const HEX = /^#[0-9a-f]{6}$/;

/** Normalises `fff`, `#FFF`, `ffffff` → `#ffffff`. Returns null if unusable. */
export function normalizeHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, '').toLowerCase();

  if (/^[0-9a-f]{3}$/.test(raw)) {
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  }

  if (/^[0-9a-f]{6}$/.test(raw)) return `#${raw}`;

  return null;
}

/**
 * Black or white, whichever reads on `hex`.
 *
 * Only used to fill a foreground token a legacy theme never carried — the
 * editor exposes all four of them explicitly. 0.179 is where WCAG contrast
 * against white and against black cross over.
 */
export function readableForeground(
  hex: string,
  onDark = '#ffffff',
  onLight = '#101519',
) {
  const normalized = normalizeHex(hex);

  if (!normalized) return onLight;

  const channel = (offset: number) => {
    const value = parseInt(normalized.slice(offset, offset + 2), 16) / 255;

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  const luminance =
    0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);

  return luminance > 0.179 ? onLight : onDark;
}

/** A colour token: repairs shorthand, falls back on anything unusable. */
const hex = (fallback: string) =>
  z
    .string()
    .transform((value) => normalizeHex(value) ?? value)
    .pipe(z.string().regex(HEX))
    .catch(fallback);

/* -------------------------------------------------------------------------
 * Vocabularies
 * ---------------------------------------------------------------------- */

export const FONT_OPTIONS = [
  {
    label: 'Playfair Display',
    value: 'Playfair Display',
    stack: '"Playfair Display", Georgia, serif',
    category: 'Serif',
  },
  {
    label: 'DM Sans',
    value: 'DM Sans',
    stack: '"DM Sans", system-ui, sans-serif',
    category: 'Sans',
  },
  {
    label: 'Inter',
    value: 'Inter',
    stack: 'Inter, system-ui, sans-serif',
    category: 'Sans',
  },
  {
    label: 'Fraunces',
    value: 'Fraunces',
    stack: 'Fraunces, Georgia, serif',
    category: 'Serif',
  },
  {
    label: 'Libre Baskerville',
    value: 'Libre Baskerville',
    stack: '"Libre Baskerville", Georgia, serif',
    category: 'Serif',
  },
  {
    label: 'Cormorant Garamond',
    value: 'Cormorant Garamond',
    stack: '"Cormorant Garamond", Georgia, serif',
    category: 'Serif',
  },
  {
    label: 'Space Grotesk',
    value: 'Space Grotesk',
    stack: '"Space Grotesk", system-ui, sans-serif',
    category: 'Display',
  },
  {
    label: 'Work Sans',
    value: 'Work Sans',
    stack: '"Work Sans", system-ui, sans-serif',
    category: 'Sans',
  },
  {
    label: 'Sora',
    value: 'Sora',
    stack: 'Sora, system-ui, sans-serif',
    category: 'Display',
  },
  {
    label: 'Outfit',
    value: 'Outfit',
    stack: 'Outfit, system-ui, sans-serif',
    category: 'Display',
  },
  {
    label: 'Lora',
    value: 'Lora',
    stack: 'Lora, Georgia, serif',
    category: 'Serif',
  },
  {
    label: 'Manrope',
    value: 'Manrope',
    stack: 'Manrope, system-ui, sans-serif',
    category: 'Sans',
  },
] as const;

const FONT_NAMES = FONT_OPTIONS.map((option) => option.value) as [
  string,
  ...string[],
];

export const RADIUS_SCALE = {
  none: '0px',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  full: '9999px',
} as const;

export const SPACING_SCALE = {
  compact: '3rem',
  normal: '5rem',
  relaxed: '7rem',
} as const;

export const CONTAINER_SCALE = {
  narrow: '64rem',
  normal: '80rem',
  wide: '96rem',
  full: '100%',
} as const;

export const SHADOW_SCALE = {
  none: 'none',
  soft: '0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)',
  medium: '0 4px 12px -2px rgb(0 0 0 / 0.10)',
  strong: '0 18px 40px -12px rgb(0 0 0 / 0.28)',
} as const;

/**
 * The editor's option lists. Exported from here rather than restated in
 * `apps/web` — the fork this module replaced drifted precisely because its
 * pickers owned their own copies of these.
 */
export const RADIUS_OPTIONS = ['none', 'sm', 'md', 'lg', 'xl', 'full'] as const;
export const CARD_RADIUS_OPTIONS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
export const BUTTON_RADIUS_OPTIONS = [
  'none',
  'sm',
  'md',
  'lg',
  'full',
] as const;
export const BUTTON_STYLE_OPTIONS = [
  'solid',
  'outline',
  'soft',
  'ghost',
] as const;
export const BUTTON_SIZE_OPTIONS = ['sm', 'md', 'lg'] as const;
export const BUTTON_WEIGHT_OPTIONS = ['400', '500', '600', '700'] as const;
export const HEADING_WEIGHT_OPTIONS = [
  '400',
  '500',
  '600',
  '700',
  '800',
] as const;
export const SHADOW_OPTIONS = ['none', 'soft', 'medium', 'strong'] as const;
export const SECTION_SPACING_OPTIONS = [
  'compact',
  'normal',
  'relaxed',
] as const;
export const CONTAINER_WIDTH_OPTIONS = [
  'narrow',
  'normal',
  'wide',
  'full',
] as const;

/* -------------------------------------------------------------------------
 * Schema
 * ---------------------------------------------------------------------- */

export const ThemeSchema = z.object({
  colors: z.object({
    background: hex('#faf8f5'),
    foreground: hex('#222a26'),
    surface: hex('#ffffff'),
    primary: hex('#396a4e'),
    primaryForeground: hex('#faf8f5'),
    accent: hex('#bf6440'),
    accentForeground: hex('#ffffff'),
    muted: hex('#efece7'),
    mutedForeground: hex('#7c7469'),
    border: hex('#e2ded7'),
    headerBackground: hex('#faf8f5'),
    headerForeground: hex('#222a26'),
    footerBackground: hex('#222a26'),
    footerForeground: hex('#faf8f5'),
    sale: hex('#b4362c'),
  }),
  typography: z.object({
    headingFont: z.enum(FONT_NAMES).catch('Playfair Display'),
    bodyFont: z.enum(FONT_NAMES).catch('DM Sans'),
    baseFontSize: z.number().min(13).max(20).catch(16),
    headingScale: z.number().min(1).max(1.6).catch(1.25),
    headingWeight: z.enum(HEADING_WEIGHT_OPTIONS).catch('700'),
    headingLetterSpacing: z.number().min(-0.05).max(0.3).catch(0),
    eyebrowLetterSpacing: z.number().min(0).max(0.4).catch(0.2),
    uppercaseHeadings: z.boolean().catch(false),
  }),
  shape: z.object({
    radius: z.enum(RADIUS_OPTIONS).catch('lg'),
    cardRadius: z.enum(CARD_RADIUS_OPTIONS).catch('xl'),
    borderWidth: z.number().min(0).max(3).catch(1),
    shadow: z.enum(SHADOW_OPTIONS).catch('soft'),
  }),
  buttons: z.object({
    style: z.enum(BUTTON_STYLE_OPTIONS).catch('solid'),
    radius: z.enum(BUTTON_RADIUS_OPTIONS).catch('full'),
    size: z.enum(BUTTON_SIZE_OPTIONS).catch('md'),
    weight: z.enum(BUTTON_WEIGHT_OPTIONS).catch('500'),
    uppercase: z.boolean().catch(false),
  }),
  layout: z.object({
    containerWidth: z.enum(CONTAINER_WIDTH_OPTIONS).catch('normal'),
    sectionSpacing: z.enum(SECTION_SPACING_OPTIONS).catch('normal'),
    gridGap: z.number().min(8).max(48).catch(24),
  }),
});

export type StorefrontTheme = z.output<typeof ThemeSchema>;

/**
 * The shipped design: every leaf's own fallback, assembled. Also the target
 * of the editor's "Reset to defaults".
 */
export const DEFAULT_THEME: StorefrontTheme = parseTheme({});

/* -------------------------------------------------------------------------
 * Reading stored themes
 * ---------------------------------------------------------------------- */

function group(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

/** Nearest container token for a legacy pixel width. */
function containerFromPx(px: number) {
  if (px <= 1040) return 'narrow';
  if (px <= 1320) return 'normal';
  if (px <= 1560) return 'wide';

  return 'full';
}

/**
 * Translates the retired dashboard dialect into this one.
 *
 * Only ever *adds* a canonical key when it is missing, so a theme already
 * written in this vocabulary passes through untouched. The `keys.length`
 * guards matter: an empty theme must land on the shipped defaults, while a
 * real palette missing a token should derive it from its siblings — a
 * merchant with a dark page wants a dark card, not the shipped white one.
 */
export function migrateTheme(value: unknown) {
  const source = group(value);

  const colors = group(source.colors);
  const typography = group(source.typography);
  const shape = group(source.shape);
  const buttons = group(source.buttons);
  const layout = group(source.layout);

  if (Object.keys(colors).length) {
    colors.sale ??= colors.destructive;
    colors.surface ??= colors.background;
    colors.accentForeground ??= derive(colors.accent);
    colors.headerForeground ??= derive(colors.headerBackground);
    colors.footerForeground ??= derive(colors.footerBackground);
  }

  typography.headingLetterSpacing ??= typography.letterSpacing;
  typography.headingWeight = asWeight(typography.headingWeight);
  buttons.weight = asWeight(buttons.weight);

  if (typeof layout.containerWidth === 'number') {
    layout.containerWidth = containerFromPx(layout.containerWidth);
  }

  return { colors, typography, shape, buttons, layout };
}

/** Font weights were numbers in the retired dialect and are tokens here. */
function asWeight(value: unknown) {
  return typeof value === 'number' ? String(value) : value;
}

function derive(background: unknown) {
  return typeof background === 'string'
    ? readableForeground(background)
    : undefined;
}

/**
 * Parses whatever came out of jsonb. Never throws, and never discards more
 * than the one token it could not read: a malformed value falls back to its
 * own default, not to the whole default palette.
 */
export function parseTheme(value: unknown): StorefrontTheme {
  return ThemeSchema.parse(migrateTheme(value));
}

/* -------------------------------------------------------------------------
 * Rendering
 * ---------------------------------------------------------------------- */

/**
 * The CSS custom property a host app binds a self-hosted face to.
 *
 * `apps/storefront/lib/fonts.ts` declares the same names through
 * `next/font`. They have to agree, so the derivation lives here next to
 * the list it derives from.
 */
export function fontVarName(family: string) {
  return `--font-${family.toLowerCase().replace(/\s+/g, '-')}`;
}

/**
 * Prefers the host's self-hosted face, falls back to the plain family.
 *
 * The `var()` fallback is load-bearing rather than belt-and-braces: a host
 * that has NOT declared these variables — the Puck canvas in `apps/web` is
 * one — would otherwise hit an undefined `var()`, which makes the whole
 * `font-family` declaration invalid at computed-value time and drops the
 * typeface entirely. With the fallback inside `var()` that host simply gets
 * the family name, exactly as before.
 */
export function fontStack(name: string) {
  const option = FONT_OPTIONS.find((entry) => entry.value === name);

  if (!option) return 'system-ui, sans-serif';

  const [primary, ...rest] = option.stack.split(',').map((part) => part.trim());

  return [`var(${fontVarName(option.value)}, ${primary})`, ...rest].join(', ');
}

/** Pixel size of each heading level, from the base size and the ratio. */
export function headingSizes(typography: StorefrontTheme['typography']) {
  const round = (value: number) => Math.round(value * 100) / 100;
  const { baseFontSize: base, headingScale: scale } = typography;

  return {
    h1: round(base * scale ** 3),
    h2: round(base * scale ** 2),
    h3: round(base * scale),
  };
}

/**
 * Token object for a `style` attribute. Emitting variables rather than
 * a stylesheet keeps the theme per-subtree, which is what lets the Puck
 * canvas preview a theme without leaking it into the dashboard chrome.
 */
export function themeToCssVars(theme: StorefrontTheme): Record<string, string> {
  const { colors, typography, shape, buttons, layout } = theme;
  const headings = headingSizes(typography);

  return {
    '--sf-background': colors.background,
    '--sf-foreground': colors.foreground,
    '--sf-surface': colors.surface,
    '--sf-primary': colors.primary,
    '--sf-primary-foreground': colors.primaryForeground,
    '--sf-accent': colors.accent,
    '--sf-accent-foreground': colors.accentForeground,
    '--sf-muted': colors.muted,
    '--sf-muted-foreground': colors.mutedForeground,
    '--sf-border': colors.border,
    '--sf-header-bg': colors.headerBackground,
    '--sf-header-fg': colors.headerForeground,
    '--sf-footer-bg': colors.footerBackground,
    '--sf-footer-fg': colors.footerForeground,
    '--sf-sale': colors.sale,

    '--sf-font-heading': fontStack(typography.headingFont),
    '--sf-font-body': fontStack(typography.bodyFont),
    '--sf-font-size': `${typography.baseFontSize}px`,
    '--sf-heading-scale': String(typography.headingScale),
    '--sf-heading-weight': typography.headingWeight,
    '--sf-heading-tracking': `${typography.headingLetterSpacing}em`,
    '--sf-eyebrow-tracking': `${typography.eyebrowLetterSpacing}em`,
    '--sf-heading-transform': typography.uppercaseHeadings
      ? 'uppercase'
      : 'none',
    '--sf-h1': `${headings.h1}px`,
    '--sf-h2': `${headings.h2}px`,
    '--sf-h3': `${headings.h3}px`,

    '--sf-radius': RADIUS_SCALE[shape.radius],
    '--sf-card-radius': RADIUS_SCALE[shape.cardRadius],
    '--sf-border-width': `${shape.borderWidth}px`,
    '--sf-shadow': SHADOW_SCALE[shape.shadow],

    '--sf-button-radius': RADIUS_SCALE[buttons.radius],
    '--sf-button-weight': buttons.weight,
    '--sf-button-transform': buttons.uppercase ? 'uppercase' : 'none',
    '--sf-button-tracking': buttons.uppercase ? '0.06em' : '0em',
    '--sf-button-height':
      buttons.size === 'sm'
        ? '2.25rem'
        : buttons.size === 'lg'
          ? '3.25rem'
          : '2.75rem',
    '--sf-button-padding':
      buttons.size === 'sm'
        ? '0 1rem'
        : buttons.size === 'lg'
          ? '0 2rem'
          : '0 1.5rem',
    '--sf-button-font-size':
      buttons.size === 'sm'
        ? '0.8125rem'
        : buttons.size === 'lg'
          ? '1rem'
          : '0.875rem',

    '--sf-container': CONTAINER_SCALE[layout.containerWidth],
    '--sf-section-spacing': SPACING_SCALE[layout.sectionSpacing],
    '--sf-grid-gap': `${layout.gridGap}px`,

    // The shadcn token names the storefront's Tailwind theme already maps
    // (`--color-background: var(--background)` and friends). Overriding them
    // here is what makes every existing utility class — `bg-background`,
    // `text-primary`, `bg-muted/30`, `rounded-lg` — follow the tenant's
    // branding without touching a single section's markup.
    '--background': colors.background,
    '--foreground': colors.foreground,
    '--card': colors.surface,
    '--card-foreground': colors.foreground,
    '--popover': colors.surface,
    '--popover-foreground': colors.foreground,
    '--primary': colors.primary,
    '--primary-foreground': colors.primaryForeground,
    '--secondary': colors.muted,
    '--secondary-foreground': colors.foreground,
    '--muted': colors.muted,
    '--muted-foreground': colors.mutedForeground,
    '--accent': colors.accent,
    '--accent-foreground': colors.accentForeground,
    '--destructive': colors.sale,
    '--border': colors.border,
    '--input': colors.border,
    '--ring': colors.primary,
    '--radius': RADIUS_SCALE[shape.radius],
    '--font-sans': fontStack(typography.bodyFont),
    '--font-heading': fontStack(typography.headingFont),
  };
}

/**
 * Button classes derived from the theme's button style. Sections use
 * this so a single Branding setting restyles every CTA at once.
 */
export function buttonStyleFor(style: StorefrontTheme['buttons']['style']) {
  switch (style) {
    case 'outline':
      return {
        background: 'transparent',
        color: 'var(--sf-primary)',
        border: 'var(--sf-border-width) solid var(--sf-primary)',
      };
    case 'soft':
      return {
        background: 'color-mix(in oklab, var(--sf-primary) 14%, transparent)',
        color: 'var(--sf-primary)',
        border: 'var(--sf-border-width) solid transparent',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: 'var(--sf-primary)',
        border: 'var(--sf-border-width) solid transparent',
      };
    default:
      return {
        background: 'var(--sf-primary)',
        color: 'var(--sf-primary-foreground)',
        border: 'var(--sf-border-width) solid transparent',
      };
  }
}
